import React from 'react';
import { FieldArray } from 'formik';
import {
    Grid, TextField, FormControl, InputLabel, Select,
    MenuItem, FormHelperText, IconButton
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    textField: {
        width: '100%',
        marginLeft: theme.spacing(1),
    },
    formControl: {
        width: '100%',
        marginLeft: theme.spacing(1),
    },
    actionButton: {
        marginTop: theme.spacing(2.5),
    }
}));

const CODE_TYPES = [
    { value: 'CPT', label: 'CPT/HCPCS' },
    { value: 'DRG', label: 'DRG' },
    { value: 'REVCODE', label: 'Revenue Code' }
];

export default function CodeFilterArray(props) {
    const { values, errors, touched, handleChange, handleBlur, setFieldTouched, setFieldValue } = props;
    const classes = useStyles();

    const getSelectedCodeTypes = (currentIndex) => {
        return values.codeFilters
            .map((filter, idx) => idx !== currentIndex ? filter.codeType : null)
            .filter(type => type && type !== '');
    };

    const getAvailableCodeTypes = (currentIndex) => {
        const selectedTypes = getSelectedCodeTypes(currentIndex);
        return CODE_TYPES.filter(type => !selectedTypes.includes(type.value));
    };

    const areAllCodeTypesSelected = () => {
        return values.codeFilters.every(filter => filter.codeType && filter.codeType !== '');
    };

    const handleCodeTypeChange = (event, index) => {
        const newValue = event.target.value;
        handleChange(event);

        if (!newValue || newValue === '') {
            setFieldValue(`codeFilters.${index}.codes`, '');
        }
    };

    return (
        <FieldArray
            name="codeFilters"
            render={({ remove, push }) => (
                <Grid container>
                    {values.codeFilters && values.codeFilters.map((filter, index) => {
                        const availableTypes = getAvailableCodeTypes(index);
                        const codeTypeError = touched.codeFilters?.[index]?.codeType &&
                                             errors.codeFilters?.[index]?.codeType;
                        const codesError = touched.codeFilters?.[index]?.codes &&
                                          errors.codeFilters?.[index]?.codes;

                        return (
                            <Grid container item xs={12} key={index} spacing={2}>
                                <Grid item xs={5.5}>
                                    <FormControl
                                        className={classes.formControl}
                                        error={Boolean(codeTypeError)}
                                        margin="normal"
                                    >
                                        <InputLabel htmlFor={`codeFilters.${index}.codeType`}>
                                            Code Type
                                        </InputLabel>
                                        <Select
                                            value={filter.codeType}
                                            onChange={(e) => handleCodeTypeChange(e, index)}
                                            onBlur={handleBlur}
                                            name={`codeFilters.${index}.codeType`}
                                            inputProps={{
                                                id: `codeFilters.${index}.codeType`
                                            }}
                                        >
                                            <MenuItem value="">
                                                <em>Select Type</em>
                                            </MenuItem>
                                            {availableTypes.map((type) => (
                                                <MenuItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {codeTypeError && (
                                            <FormHelperText error>
                                                {codeTypeError}
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={4.5}>
                                    <TextField
                                        label="Codes"
                                        name={`codeFilters.${index}.codes`}
                                        style={{ marginLeft: '36px',paddingRight:"10px" }}
                                        className={classes.textField}
                                        margin="normal"
                                        value={filter.codes}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        helperText={codesError}
                                        error={Boolean(codesError)}
                                        disabled={!filter.codeType}
                                        placeholder={filter.codeType ?
                                            "e.g., 99201,99202,99203" :
                                            "Select code type first"}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    {index === 0 && values.codeFilters.length < 3 && (
                                        <IconButton
                                            className={classes.actionButton}
                                            onClick={() => {
                                                // Mark all existing fields as touched to show validation errors
                                                values.codeFilters.forEach((_, idx) => {
                                                    setFieldTouched(`codeFilters.${idx}.codeType`, true);
                                                    setFieldTouched(`codeFilters.${idx}.codes`, true);
                                                });
                                                // Add new empty row
                                                push({ codeType: '', codes: '' });
                                            }}
                                            color="primary"
                                            aria-label="add code filter"
                                            size="large"
                                            disabled={!areAllCodeTypesSelected()}
                                        >
                                            <AddCircleIcon />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        className={classes.actionButton}
                                        onClick={() => remove(index)}
                                        aria-label="remove code filter"
                                        size="large"
                                        disabled={values.codeFilters.length === 1}
                                        sx={{ color: 'grey.600' }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        />
    );
}
